import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { bodyMessageOf, messageOf, statusOf } from 'utils/apiError';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import intl from 'react-intl-universal';

import { ARTICLE_SERVICE_KEY, Article, Assignment } from 'assignment/Assignment';
import { ArticleService, ASSIGNMENT_SERVICE, AssignmentService } from 'assignment/service';
import { USER_SERVICE, UserService } from 'user/UserService';
import { injector } from 'Injector';
import { EditableEvaluation, EvaluationAnswer, AssignmentEvaluationList } from 'evaluation/Evaluation';
import { ASSIGNMENT_EVALUATION_SERVICE, AssignmentEvaluationService } from 'evaluation/service';
import { debounce } from 'utils/debounce';
import { SAVE_DELAY, SHOW_MESSAGE_DELAY, STATUS_FORBIDDEN } from 'utils/constants';
import { Notification, NotificationTypes } from 'components/common/Notification/Notification';
import { StoreState, Locales } from 'utils/enums';
import { reportFailure } from 'utils/reportFailure';

const passedMark = 2;

export class AssignmentEvaluationStore {

  @computed
  public get currentEvaluation() {
    return this._currentEvaluation;
  }

  @computed
  public get paginationTotalPages(): number {
    return this._paginationTotalPages;
  }

  @computed
  public get getListOfAnswers() {
    return toJS(this.answersList);
  }

  @computed
  public get evaluationAnswers() {
    return this._evaluationAnswers;
  }

  @computed
  public get displaySaveMessage() {
    return this._displaySaveMessage;
  }

  @computed
  public get evaluationCurrentPage() {
    return this.answersListService.filter.page;
  }

  private userService: UserService = injector.get(USER_SERVICE);
  private evaluationService: AssignmentEvaluationService = injector.get<AssignmentEvaluationService>(ASSIGNMENT_EVALUATION_SERVICE);
  private assignmentService: AssignmentService = injector.get<AssignmentService>(ASSIGNMENT_SERVICE);
  private answersListService: AssignmentEvaluationList = new AssignmentEvaluationList();
  private articleService: ArticleService = injector.get<ArticleService>(ARTICLE_SERVICE_KEY);

  // Through an arrow, so `this` is the store however the wrapper is called, and the method is
  // read when it runs, after makeObservable has wrapped it as an action.
  private save = debounce(() => { void this.saveEvaluation(); }, SAVE_DELAY);

  @observable private _evaluationAnswers: EvaluationAnswer | undefined = undefined;
  @observable public _paginationTotalPages: number = 1;
  @observable public _currentEvaluation: EditableEvaluation | null = null;
  @observable public answersList: Array<EditableEvaluation> = [];
  @observable public _displaySaveMessage: boolean = false;
  @observable public relatedArticles: Array<Article> = [];
  @observable public _currentAssignment: Assignment | null = null;

  @observable public answersListState = StoreState.PENDING;
  @observable public currentEntityState = StoreState.PENDING;
  @observable public relatedArticlesState: StoreState = StoreState.PENDING;

  constructor() {
    makeObservable(this);
  }

  public get currentEntity(): Assignment | null {
    return this._currentAssignment;
  }

  public get currentEvaluationId(): number | undefined {
    if (this._currentEvaluation) {
      return this._currentEvaluation.answerId;
    }
    return undefined;
  }

  /**
   * Whether the entity loaded. A failure ends in ERROR and is reported here, like the list fetches
   * (#193); the answer is returned because AnswersList goes on to load answers by the entity's id,
   * and has to stop instead.
   */
  @action
  public async getCurrentEntity(id: number): Promise<boolean> {
    this.currentEntityState = StoreState.LOADING;
    try {
      const assignment = await this.assignmentService.getAssignmentById(id);

      runInAction(() => {
        this._currentAssignment = assignment;
        this.currentEntityState = StoreState.PENDING;
      });
      return true;
    } catch (error) {
      runInAction(() => { this.currentEntityState = StoreState.ERROR; });
      reportFailure(error);
      return false;
    }
  }

  @action
  public getAnswersByPage(page: number, id: number) {
    this.answersListService.setFiltersPage(page);
    this.getAnswersList(id);
  }

  @action
  public calcPassedStatus() {
    return this.currentEvaluation && !isNull(this.currentEvaluation.mark) && this.currentEvaluation.mark >= passedMark;
  }

  @action
  public setMark(mark: number | null) {
    if (this._currentEvaluation) {
      this._currentEvaluation.setMark(mark);
      const isPassed = this.calcPassedStatus();
      if (isNull(mark)) {
        this._currentEvaluation.setPassedStatus(null);
      } else if (!isNil(isPassed)) {
        this._currentEvaluation.setPassedStatus(isPassed);
      }
      this.saveEvaluation();
    }
  }

  @action
  public setCommentToAnswer(comment: string, questionId: number) {
    if (this._currentEvaluation) {
      this._currentEvaluation.setCommentToAnswer(comment, questionId);
      this.save();
    }
  }

  @action
  public setCommentToEntity(comment: string) {
    if (this._currentEvaluation) {
      this._currentEvaluation.setCommentToEntity(comment);
      this.save();
    }
  }

  @action
  public setStatus(status: boolean | null) {
    if (this._currentEvaluation) {
      this._currentEvaluation.setStatus(status);
      this.saveEvaluation();
    }
  }

  @action
  public setCurrentEvaluation(id?: number) {
    if (id) {
      return this._currentEvaluation = this.answersList.find(item => item.answerId === id)!;
    }
    return this._currentEvaluation = null;
  }

  @action
  public async getAnswersList(id: number) {
    this.answersListState = StoreState.LOADING;
    try {
      const response = await this.answersListService.getListOfAnswersToAssignment(id);

      runInAction(() => {
        this.answersList = response.listAnswers;
        this._paginationTotalPages = response.total_pages;
        this.answersListState = StoreState.PENDING;
      });
    } catch (error) {
      runInAction(() => { this.answersListState = StoreState.ERROR; });
      reportFailure(error);
    }
  }

  @computed
  public get currentLocale() {
    return this.userService.getCurrentLocale() || Locales.EN;
  }

  @action
  public async setFiltersPage(number: number) {
    this.answersListService.setFiltersPage(number);
    await this.getAnswersList(this._currentAssignment!.id);
  }

  @action
  public getAnswersById = async () => {
    // Cleared here, at the start of the load, rather than from CheckingPanel's unmount.
    // That component never loaded this value, and clearing state it did not acquire is not
    // StrictMode-safe: React mounts, unmounts and remounts every component in development, so
    // the teardown ran while the panel was opening and wiped the answers its parent had just
    // fetched. Doing it here also closes the gap the unmount never covered — the previous
    // student's answers showing while the next student's request is still in flight.
    runInAction(() => { this._evaluationAnswers = undefined; });

    if (this._currentEvaluation) {
      this._evaluationAnswers = await this.evaluationService.getAnswersById(this._currentAssignment!.id, this._currentEvaluation.answerId);
      if (this._evaluationAnswers.comment) {
        this._currentEvaluation.setCommentToEntity(this._evaluationAnswers.comment);
      }
      this._evaluationAnswers.revisionContent.answers.forEach((answer) => {
        if (answer.comment) {
          this._currentEvaluation!.setCommentToAnswer(answer.comment, answer.answerBlock.key.id!);
        }
      });
    }
    this.getRelatedArticles();
  }

  @action
  public getAnswersByIdForStudent = async (assignmentId: number, answerId: number) => {
    const response = await this.evaluationService.getAnswersByIdForStudent(assignmentId, answerId);
    this._evaluationAnswers = response.answerInfo;
    this._currentEvaluation = response.evaluation;
  }

  @action
  public getDraftAnswersById = async () => {
    if (this._currentEvaluation) {
      const {
        uuid,
        commentToEntity
      } = await this.evaluationService.getDraftAnswersById(this._currentAssignment!.id, this._currentEvaluation.answerId);
      this._currentEvaluation.setUuid(uuid);
      if (!isNull(commentToEntity)) {
        this._currentEvaluation.setCommentToEntity(commentToEntity);
      }
    }
  }

  @action
  public showSaveMessage = () => {
    this._displaySaveMessage = true;
    setTimeout(() => this._displaySaveMessage = false, SHOW_MESSAGE_DELAY);
  }

  /**
   * Reports a failed save instead of letting it reject into nothing.
   *
   * Every caller of this is fire-and-forget — `setMark` and `setStatus` call it directly and
   * the comment fields go through a debounced wrapper — and the repo method underneath is a
   * bare `await API.put(...)`. So a save that failed produced an unhandled rejection, no
   * notification, and a teacher who had every reason to think the grade was recorded.
   *
   * `showSaveMessage` only runs on success, which is the other half: silence meant failure
   * and nobody could tell.
   */
  public async saveEvaluation() {
    if (this._currentEvaluation) {
      try {
        await this.evaluationService.saveEvaluation(this._currentAssignment!.id, this._currentEvaluation.answerId, this.currentEvaluation!);
        this.showSaveMessage();
      } catch (error) {
        Notification.create({
          type: NotificationTypes.ERROR,
          title: bodyMessageOf(error) ?? messageOf(error)
        });
      }
    }
  }

  @action
  /**
   * Whether the evaluation was published. Every failure used to resolve all the same: a missing
   * mark, a deadline that had passed, a request that never arrived (that one only in the
   * console). The checking panel then closed as if it had been saved (#223).
   */
  public publishEvaluation = async (): Promise<boolean> => {
    if (isNull(this.currentEvaluation!.mark) && isNull(this.currentEvaluation!.isPassed)) {
      Notification.create({
        type: NotificationTypes.ERROR,
        title: intl.get('answers.validation.markOrStatus')
      });
      return false;
    }
    try {
      await this.evaluationService.publishEvaluation(this._currentAssignment!.id, this._currentEvaluation!.answerId, this.currentEvaluation!);
      return true;
    } catch (error) {
      if (statusOf(error) === STATUS_FORBIDDEN) {
        Notification.create({
          type: NotificationTypes.ERROR,
          title: intl.get('answers.validation.deadline')
        });
      } else if (error instanceof EvaluationValidationError) {
        Notification.create({
          type: NotificationTypes.ERROR,
          title: intl.get('answers.validation.mark')
        });
      } else {
        reportFailure(error);
      }
      return false;
    }
  }

  public async getRelatedArticles() {
    try {
      this.relatedArticlesState = StoreState.LOADING;

      const ids = this.evaluationAnswers!.readArticleData.map(article => article.wpId!);
      if (ids && ids.length) {
        const articles = await this.articleService.getArticlesByIds(ids);

        const allArticles = this.evaluationAnswers!.readArticleData.map((article) => {
          const fullArticle = articles.find(item => item.id === article.wpId!);
          return {
            ...article,
            ...fullArticle,
            readLevel: article.readLevel,
          };
        });

        this.relatedArticlesState = StoreState.PENDING;
        return this.relatedArticles = allArticles;
      }

      this.relatedArticlesState = StoreState.PENDING;
      return this.relatedArticles = [];
    } catch (e) {
      // Reported, not rethrown: no caller caught it (#216).
      runInAction(() => { this.relatedArticlesState = StoreState.ERROR; });
      reportFailure(e);
      return [];
    }
  }
}

export class EvaluationValidationError extends Error {
  public readonly localizationKey: string;

  constructor(localizationKey: string) {
    super(localizationKey);
    this.localizationKey = localizationKey;
  }
}
